import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Building2, ChevronDown, LogOut, Menu, Moon, Search, Sun, User, X,
} from 'lucide-react';
import { NAVIGATION, type NavItem } from '@/app/navigation';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useAccess } from '@/hooks/useAccess';
import { useDocumentTitle, useSystemName } from '@/hooks/useSystemName';
import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';
import { Badge, Button, Select, Spinner } from '@/components/ui';
import { SubscriptionBanner } from '@/components/SubscriptionBanner';
import {
  branchSelected,
  organizationSelected,
  themeChanged,
  type ThemePreference,
} from '@/features/tenant/tenantSlice';
import { loggedOut } from '@/features/auth/authSlice';
import { useLogoutMutation } from '@/api/endpoints';
import { baseApi } from '@/api/baseApi';

export function AppShell() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  // A route change on mobile should close the drawer.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-full min-h-screen bg-sunken">
      <Sidebar open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onMenuClick={() => setMobileNavOpen(true)} />
        <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-7xl">
            <SubscriptionBanner />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { pathname } = useLocation();
  const { can, canAny, hasFeature } = useAccess();
  const organization = useAppSelector((state) => state.tenant.context?.organization);
  const systemName = useSystemName();
  useDocumentTitle();

  /**
   * The sidebar is generated from the same table the route guards use, filtered
   * by the permissions and features the API reported for this member.
   */
  /**
   * Which sections are unfolded. The one holding the current page opens itself,
   * so following a link never leaves the reader looking at a collapsed list
   * with no idea where they are.
   */
  const [openSections, setOpenSections] = useState<string[]>([]);

  useEffect(() => {
    const current = NAVIGATION.find((section) =>
      section.items.some((item) => pathname.startsWith(item.to)),
    );
    if (current) {
      setOpenSections((sections) =>
        sections.includes(current.label) ? sections : [...sections, current.label],
      );
    }
  }, [pathname]);

  const toggleSection = (label: string): void =>
    setOpenSections((current) =>
      current.includes(label)
        ? current.filter((entry) => entry !== label)
        : [...current, label],
    );

  const sections = useMemo(
    () =>
      NAVIGATION.map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (item.feature && !hasFeature(item.feature)) return false;
          if (!item.permissions || item.permissions.length === 0) return true;
          return item.permissions.length === 1
            ? can(...item.permissions)
            : canAny(...item.permissions);
        }),
      })).filter((section) => section.items.length > 0),
    [can, canAny, hasFeature],
  );

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onClose} aria-hidden />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r border-subtle',
          'transition-transform lg:static lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        style={{ background: 'var(--surface-raised)' }}
        aria-label="Main navigation"
      >
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-subtle px-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-600 font-bold text-white">
              {systemName[0]?.toUpperCase() ?? 'S'}
            </div>
            <div className="min-w-0">
              <p className="truncate font-semibold leading-tight">{systemName}</p>
              <p className="truncate text-xs text-muted">{organization?.name ?? '—'}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 hover:bg-sunken lg:hidden"
            aria-label="Close navigation"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {sections.map((section) => {
            const SectionIcon = section.icon;
            // The section holding the current page is open; the rest are folded
            // away. With a dozen modules, a flat list is a wall of links.
            const isOpen = openSections.includes(section.label);

            return (
              <div key={section.label}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => toggleSection(section.label)}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted hover:bg-sunken"
                >
                  {SectionIcon && <SectionIcon className="size-4 shrink-0" aria-hidden />}
                  <span className="flex-1 text-left">{section.label}</span>
                  <ChevronDown
                    className={cn('size-4 shrink-0 transition-transform', !isOpen && '-rotate-90')}
                    aria-hidden
                  />
                </button>

                {isOpen && (
                  <ul className="mt-0.5 space-y-0.5 pb-2">
                    {section.items.map((item) => (
                      <li key={item.to}>
                        <SidebarLink item={item} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
}

function SidebarLink({ item }: { item: NavItem }) {
  const Icon = item.icon;

  // Modules that are not built yet are shown, disabled, with the phase they
  // belong to — never as an empty screen pretending to be finished.
  if (item.phase) {
    return (
      <div
        className="flex cursor-not-allowed items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-muted opacity-60"
        title={`Planned for phase ${item.phase}`}
      >
        {Icon && <Icon className="size-4 shrink-0" aria-hidden />}
        <span className="flex-1 truncate">{item.label}</span>
        <Badge tone="neutral" className="shrink-0">P{item.phase}</Badge>
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-900/40 dark:text-brand-100'
            : 'text-secondary hover:bg-sunken',
        )
      }
    >
      {Icon && <Icon className="size-4 shrink-0" aria-hidden />}
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

function Topbar({ onMenuClick }: { onMenuClick: () => void }) {
  return (
    <header
      className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b border-subtle px-4 sm:px-6"
      style={{ background: 'var(--surface-raised)' }}
    >
      <button
        type="button"
        onClick={onMenuClick}
        className="rounded-lg p-2 hover:bg-sunken lg:hidden"
        aria-label="Open navigation"
      >
        <Menu className="size-5" aria-hidden />
      </button>

      <BranchSwitcher />

      <div className="ml-auto flex items-center gap-1.5">
        <GlobalSearchButton />
        <ThemeToggle />
        <AccountMenu />
      </div>
    </header>
  );
}

function BranchSwitcher() {
  const dispatch = useAppDispatch();
  const branches = useAppSelector((state) => state.tenant.context?.branches ?? []);
  const branchId = useAppSelector((state) => state.tenant.branchId);

  if (branches.length === 0) return <div className="text-sm text-muted">No branch</div>;

  // With a single branch there is nothing to switch between.
  if (branches.length === 1) {
    return (
      <div className="flex items-center gap-2 text-sm text-secondary">
        <Building2 className="size-4" aria-hidden />
        <span className="truncate">{branches[0]?.name}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      <Building2 className="size-4 text-muted" aria-hidden />
      <div className="w-48">
        <Select
          aria-label="Active branch"
          value={branchId ?? ''}
          onChange={(event) => dispatch(branchSelected(event.target.value || null))}
          className="h-9"
        >
          {branches.map((branch) => (
            <option key={branch.id} value={branch.id}>{branch.name}</option>
          ))}
        </Select>
      </div>
    </div>
  );
}

function GlobalSearchButton() {
  return (
    <button
      type="button"
      className="hidden items-center gap-2 rounded-lg border border-subtle px-3 py-2 text-sm text-muted hover:bg-sunken sm:flex"
      title="Global search arrives with the catalog module"
      disabled
    >
      <Search className="size-4" aria-hidden />
      <span>Search</span>
    </button>
  );
}

function ThemeToggle() {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.tenant.theme);

  const next: Record<ThemePreference, ThemePreference> = {
    system: 'light',
    light: 'dark',
    dark: 'system',
  };

  return (
    <button
      type="button"
      onClick={() => dispatch(themeChanged(next[theme]))}
      className="rounded-lg p-2 hover:bg-sunken"
      aria-label={`Theme: ${theme}. Switch to ${next[theme]}.`}
      title={`Theme: ${theme}`}
    >
      {theme === 'dark' ? <Moon className="size-5" aria-hidden /> : <Sun className="size-5" aria-hidden />}
    </button>
  );
}

function AccountMenu() {
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const organizations = useAppSelector((state) => state.auth.organizations);
  const organizationId = useAppSelector((state) => state.tenant.organizationId);
  const [logout, { isLoading }] = useLogoutMutation();

  const signOut = async (): Promise<void> => {
    // Best effort: even if the API call fails, the local session must end.
    await logout().unwrap().catch(() => undefined);
    dispatch(loggedOut());
    dispatch(baseApi.util.resetApiState());
    navigate('/auth/login', { replace: true });
  };

  const switchOrganization = (id: string): void => {
    dispatch(organizationSelected(id));
    dispatch(baseApi.util.resetApiState());
    setOpen(false);
    navigate('/app/dashboard');
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 rounded-lg p-1.5 hover:bg-sunken"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="grid size-8 place-items-center rounded-full bg-brand-600 text-xs font-semibold text-white">
          {initials(user?.firstName, user?.lastName)}
        </span>
        <ChevronDown className="hidden size-4 text-muted sm:block" aria-hidden />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="menu"
            className="surface absolute right-0 z-20 mt-2 w-64 rounded-xl p-1.5 shadow-lg"
          >
            <div className="border-b border-subtle px-3 py-2.5">
              <p className="truncate text-sm font-medium">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="truncate text-xs text-muted">{user?.email}</p>
            </div>

            {organizations.length > 1 && (
              <div className="border-b border-subtle py-1.5">
                <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted">
                  Businesses
                </p>
                {organizations.map((organization) => (
                  <button
                    key={organization.id}
                    type="button"
                    role="menuitem"
                    onClick={() => switchOrganization(organization.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-sunken',
                      organization.id === organizationId && 'font-medium text-brand-700 dark:text-brand-200',
                    )}
                  >
                    <Building2 className="size-4 shrink-0" aria-hidden />
                    <span className="truncate">{organization.name}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="py-1">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  navigate('/app/profile');
                }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-sunken"
              >
                <User className="size-4" aria-hidden />
                Profile & security
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => void signOut()}
                disabled={isLoading}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-sunken dark:text-red-400"
              >
                {isLoading ? <Spinner className="size-4" /> : <LogOut className="size-4" aria-hidden />}
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export { Button };
